import { Checkbox, Table } from 'antd';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';
import type { CustomLayerWidgetProps } from '@/components/MapGeneration/layers/patterns';
import { deleteShapeFile, setLayerSettings } from '@/components/MapGeneration/store/actions';
import { MODALS } from '@/components/MapGeneration/store/constants';
import { state } from '@/components/MapGeneration/store/state';
import { InputNumber } from '@/shared/MiInputNumber/MiInputNumber';

import { DeleteSVG } from '../icons';

interface ContourProps {
    field_id: number;
    brush_color: string;
    brush_visible: boolean;
    contour_id: number;
    contour_type_id: number;
    marker_visible: boolean;
    pen_color: string;
    pen_visible: boolean;
    pen_width: number;
    pen_style: number;
    stratum_id: number;
    visible: boolean;
}

interface PrivateContourProps {
    field_id: number;
    field_visible: boolean;
    stratum_id: number;
    stratum_name: string;
    stratum_visible: boolean;
    contour_type_id: number;
    contour_type_name: string;
    contour_name: string;
    contour_type_visible: boolean;
    contour_id: number;
    contour_visible: boolean;
}

// TODO: Задействовать, когда будет работа с несколькими месторождениями

// const getFields = (contours: ContourProps[]): number[] => [
//     ...new Set(contours.sort((a, b) => a.field_id - b.field_id).map(({ field_id }) => field_id)),
// ];

const getStratums = (
    contours: PrivateContourProps[],
    fieldIds: number[],
): Array<{ id: number; name: string; visible: boolean }> => {
    const result: Array<{ id: number; name: string; visible: boolean }> = [];
    contours
        .sort((a, b) => a.stratum_id - b.stratum_id)
        .filter(a => fieldIds.includes(a.field_id))
        .forEach(e => {
            if (result.findIndex(r => r.id === e.stratum_id) === -1) {
                result.push({
                    id: e.stratum_id,
                    name: e.stratum_name,
                    visible: e.stratum_visible,
                });
            }
        });

    return result;
};

const getContourTypes = (
    contours: PrivateContourProps[],
    fieldIds: number[],
    stratumId: number,
): Array<{ id: number; name: string; visible: boolean }> => {
    const result: Array<{ id: number; name: string; visible: boolean }> = [];
    contours
        .sort((a, b) => a.stratum_id - b.stratum_id)
        .filter(a => fieldIds.includes(a.field_id))
        .filter(a => a.stratum_id === stratumId)
        .forEach(e => {
            if (result.findIndex(r => r.id === e.contour_type_id) === -1) {
                result.push({
                    id: e.contour_type_id,
                    name: e.contour_type_name,
                    visible: e.contour_type_visible,
                });
            }
        });
    return result;
};

const getContours = (
    contours: PrivateContourProps[],
    fieldIds: number[],
    stratumId: number,
    contourTypeId: number,
): PrivateContourProps[] => {
    return contours.filter(
        a => fieldIds.includes(a.field_id) && a.stratum_id === stratumId && a.contour_type_id === contourTypeId,
    );
};

const getContourTableParams = (
    contours: ContourProps[],
    contourId: number,
): Array<{
    key: string;
    name: string;
    width?: number;
    style?: number;
    color: string;
    visible: boolean;
}> => {
    const contour = contours.find(e => e.contour_id === contourId);
    if (contour !== undefined) {
        return [
            {
                key: 'brush',
                name: 'Кисть',
                color: contour.brush_color,
                visible: contour.brush_visible,
            },
            {
                key: 'pen',
                name: 'Обводка',
                color: contour.pen_color,
                visible: contour.pen_visible,
                width: contour.pen_width,
                style: contour.pen_style,
            },
        ];
    }
    return [];
};

const getColor = (color: string): { hex: string; r: number; g: number; b: number; alpha: number } => {
    return {
        hex: `#${color.slice(3)}`,
        r: Number(`0x${color.slice(3, 5)}`),
        g: Number(`0x${color.slice(5, 7)}`),
        b: Number(`0x${color.slice(7, 9)}`),
        alpha: Number(`0x${color.slice(1, 3)}`),
    };
};

const setColor = (color: string, alpha: number): string => `#${alpha.toString(16)}${color.slice(1)}`;

export const ContourSettings = ({ layer }: CustomLayerWidgetProps) => {
    const $state = useSnapshot(state);
    const [contours, setContours] = useState<ContourProps[]>([]);
    const [privateContours, setPrivateContours] = useState<PrivateContourProps[]>([]);
    const [shapeFiles, setShapeFiles] = useState<Array<{ id: number; file_name: string; visible: boolean }>>([]);

    useEffect(() => {
        if (state.layerSettings.has(layer.guid)) {
            /* @ts-ignore */
            setContours(state.layerSettings.get(layer.guid)?.contour_settings ?? []);
            /* @ts-ignore */
            setPrivateContours(state.layerSettings.get(layer.guid)?.private_contour_settings ?? []);
            /* @ts-ignore */
            const shapes = (state.layerSettings.get(layer.guid)?.shape_files_settings ?? []) as Array<{
                id: number;
                file_name: string;
                visible: boolean;
            }>;
            // safeguard: ensure unique by id and stable ordering by title
            const uniqueById = Array.from(new Map(shapes.map(s => [s.id, s])).values()) as Array<{
                id: number;
                file_name: string;
                visible: boolean;
            }>;
            uniqueById.sort((a, b) => String(a.file_name).localeCompare(String(b.file_name)));
            setShapeFiles(uniqueById);
        } else {
            setContours([]);
            setPrivateContours([]);
            setShapeFiles([]);
        }
    }, [layer, $state.layerSettings]);

    const applyContourVisibility = (
        newVal: boolean,
        stratumId?: number,
        contourTypeId?: number,
        contourId?: number,
    ): void => {
        const newPrivateContours = privateContours.map(pc => {
            if (stratumId !== undefined) {
                if (pc.stratum_id === stratumId) {
                    if (contourTypeId !== undefined) {
                        if (pc.contour_type_id === contourTypeId) {
                            if (contourId !== undefined) {
                                if (pc.contour_id === contourId) {
                                    return { ...pc, contour_visible: newVal };
                                }
                            } else {
                                return {
                                    ...pc,
                                    contour_visible: newVal,
                                    contour_type_visible: newVal,
                                };
                            }
                        }
                    } else {
                        return {
                            ...pc,
                            stratum_visible: newVal,
                            contour_type_visible: newVal,
                            contour_visible: newVal,
                        };
                    }
                }
            }
            return { ...pc };
        });

        const newContours = contours.map(ctr => {
            let newVisible = ctr.visible;
            const priv = newPrivateContours.find(pc => pc.contour_id === ctr.contour_id);
            if (priv !== undefined) {
                newVisible = priv.contour_visible && priv.contour_type_visible && priv.stratum_visible;
            }
            return { ...ctr, visible: newVisible };
        });
        const settings: Record<string, unknown> = {
            contour_settings: newContours,
            private_contour_settings: newPrivateContours,
            shape_files_settings: shapeFiles,
        };
        setLayerSettings(layer, settings);
    };

    const applyContourStyle = (
        contourId: number,
        newSets: {
            brush_visible?: boolean;
            brush_color?: string;
            pen_visible?: boolean;
            pen_color?: string;
            pen_width?: number;
        },
    ) => {
        const newContours = contours.map(contour => {
            if (contour.contour_id === contourId) {
                return {
                    ...contour,
                    brush_visible: newSets.brush_visible !== undefined ? newSets.brush_visible : contour.brush_visible,
                    brush_color: newSets.brush_color !== undefined ? newSets.brush_color : contour.brush_color,
                    pen_visible: newSets.pen_visible !== undefined ? newSets.pen_visible : contour.pen_visible,
                    pen_color: newSets.pen_color !== undefined ? newSets.pen_color : contour.pen_color,
                    pen_width: newSets.pen_width !== undefined ? newSets.pen_width : contour.pen_width,
                };
            }
            return { ...contour };
        });

        const settings: Record<string, unknown> = {
            contour_settings: newContours,
            private_contour_settings: privateContours,
            shape_files_settings: shapeFiles,
        };

        setLayerSettings(layer, settings);
    };

    const applyShapeVisibility = (newVal: boolean, shapeId: number) => {
        const updatedShapeFiles = shapeFiles.map(shape =>
            shape.id === shapeId ? { ...shape, visible: newVal } : shape,
        );

        const settings: Record<string, unknown> = {
            contour_settings: contours,
            private_contour_settings: privateContours,
            shape_files_settings: updatedShapeFiles,
        };

        setLayerSettings(layer, settings);
    };

    if (isEmpty($state.fields)) {
        return <div></div>;
    }

    return (
        <div className="min-h-1/2 flex h-1/2 flex-col overflow-auto border-t border-gray-500 dark:border-fill-primary h-fit">
            <div className="flex w-full shrink-0 grow flex-col overflow-y-auto bg-white pb-2 dark:bg-block-primary">
                <Table
                    showHeader={false}
                    pagination={false}
                    size="small"
                    // expandable={{
                    //     defaultExpandAllRows: false,
                    // }}
                    className="customAntdTable"
                    columns={[
                        {
                            title: '',
                            key: 'name',
                            render: (_, record) => (
                                <Checkbox
                                    defaultChecked={record.visible}
                                    onChange={event => {
                                        applyContourVisibility(event.target.checked, record.key);
                                    }}
                                />
                            ),
                        },
                        {
                            title: '',
                            dataIndex: 'name',
                            key: 'name',
                            width: '100%',
                        },
                    ]}
                    dataSource={getStratums(
                        privateContours,
                        state.fields.map(({ id }) => id),
                    ).map(stratum => ({
                        key: stratum.id,
                        name: stratum.name,
                        visible: stratum.visible,
                    }))}
                    expandable={{
                        expandedRowRender: recordStratum => (
                            <Table
                                showHeader={false}
                                pagination={false}
                                size="small"
                                // defaultExpandAllRows={false}
                                columns={[
                                    {
                                        title: '',
                                        key: 'name',
                                        render: (_, record) => (
                                            <Checkbox
                                                checked={record.visible}
                                                disabled={!recordStratum.visible}
                                                onChange={event => {
                                                    applyContourVisibility(
                                                        event.target.checked,
                                                        recordStratum.key,
                                                        record.key,
                                                    );
                                                }}
                                            />
                                        ),
                                    },
                                    {
                                        title: '',
                                        dataIndex: 'name',
                                        key: 'name',
                                        width: '100%',
                                    },
                                ]}
                                dataSource={getContourTypes(
                                    privateContours,
                                    state.fields.map(({ id }) => id),
                                    recordStratum.key,
                                ).map(contourType => ({
                                    key: contourType.id,
                                    name: contourType.name,
                                    visible: contourType.visible,
                                }))}
                                expandable={{
                                    expandedRowRender: recordContourType => (
                                        <Table
                                            showHeader={false}
                                            pagination={false}
                                            size="small"
                                            className="customAntdTable"
                                            columns={[
                                                {
                                                    title: '',
                                                    key: 'name',
                                                    render: (_, record) => (
                                                        <Checkbox
                                                            checked={record.visible}
                                                            disabled={!recordContourType.visible}
                                                            onChange={event =>
                                                                applyContourVisibility(
                                                                    event.target.checked,
                                                                    recordStratum.key,
                                                                    recordContourType.key,
                                                                    record.key,
                                                                )
                                                            }
                                                        />
                                                    ),
                                                },
                                                {
                                                    title: '',
                                                    dataIndex: 'title',
                                                    key: 'title',
                                                    align: 'left',
                                                    width: '100%',
                                                },
                                            ]}
                                            dataSource={getContours(
                                                privateContours,
                                                state.fields.map(({ id }) => id),
                                                recordStratum.key,
                                                recordContourType.key,
                                            ).map(contour => ({
                                                key: contour.contour_id,
                                                title: contour.contour_name,
                                                visible: contour.contour_visible,
                                            }))}
                                            expandable={{
                                                expandedRowRender: recordContour => (
                                                    <Table
                                                        showHeader={false}
                                                        pagination={false}
                                                        size="small"
                                                        className="customAntdTable"
                                                        columns={[
                                                            {
                                                                title: '',
                                                                key: 'name',
                                                                render: (_, record) => (
                                                                    <Checkbox
                                                                        checked={record.visible}
                                                                        disabled={!recordContour.visible}
                                                                        onChange={event => {
                                                                            if (record.key === 'brush') {
                                                                                applyContourStyle(recordContour.key, {
                                                                                    brush_visible: event.target.checked,
                                                                                });
                                                                            }
                                                                            if (record.key === 'pen') {
                                                                                applyContourStyle(recordContour.key, {
                                                                                    pen_visible: event.target.checked,
                                                                                });
                                                                            }
                                                                        }}
                                                                    />
                                                                ),
                                                            },
                                                            {
                                                                title: '',
                                                                dataIndex: 'name',
                                                                align: 'left',
                                                                key: 'name',
                                                                width: '100%',
                                                            },
                                                            {
                                                                title: '',
                                                                key: 'name',
                                                                render: (_, record) => {
                                                                    if (record.width !== undefined) {
                                                                        return (
                                                                            <InputNumber
                                                                                step={1}
                                                                                defaultValue={Number(record.width)}
                                                                                disabled={!recordContour.visible}
                                                                                size="small"
                                                                                className="w-11"
                                                                                onChange={newVal => {
                                                                                    if (record.key === 'pen') {
                                                                                        applyContourStyle(
                                                                                            recordContour.key,
                                                                                            {
                                                                                                pen_width:
                                                                                                    Number(newVal),
                                                                                            },
                                                                                        );
                                                                                    }
                                                                                }}
                                                                            />
                                                                        );
                                                                    }
                                                                    return <div></div>;
                                                                },
                                                            },
                                                            {
                                                                title: '',
                                                                key: 'name',
                                                                render: (_, record) => (
                                                                    <div
                                                                        className="flex items-center"
                                                                        onClick={() => {
                                                                            state.colorPickerData = {
                                                                                defaultColor: getColor(
                                                                                    String(record.color),
                                                                                ).hex,
                                                                                defaultAlpha: getColor(
                                                                                    String(record.color),
                                                                                ).alpha,
                                                                                onApply: (
                                                                                    newColor: string,
                                                                                    newAlpha: number,
                                                                                ) => {
                                                                                    state.modal = null;
                                                                                    if (record.key === 'brush') {
                                                                                        applyContourStyle(
                                                                                            recordContour.key,
                                                                                            {
                                                                                                brush_color: setColor(
                                                                                                    newColor,
                                                                                                    newAlpha,
                                                                                                ),
                                                                                            },
                                                                                        );
                                                                                    }
                                                                                    if (record.key === 'pen') {
                                                                                        applyContourStyle(
                                                                                            recordContour.key,
                                                                                            {
                                                                                                pen_color: setColor(
                                                                                                    newColor,
                                                                                                    newAlpha,
                                                                                                ),
                                                                                            },
                                                                                        );
                                                                                    }
                                                                                },
                                                                                onCancel: () => {
                                                                                    state.modal = null;
                                                                                },
                                                                            };
                                                                            if (!recordContour.visible) {
                                                                                return;
                                                                            }
                                                                            state.modal = MODALS.COLOR_PICKER;
                                                                        }}
                                                                    >
                                                                        <div
                                                                            className={`${
                                                                                !recordContour.visible
                                                                                    ? 'cursor-not-allowed'
                                                                                    : ''
                                                                            } ${`h-5 w-5 border border-gray-500`}`}
                                                                            style={{
                                                                                backgroundColor: getColor(
                                                                                    String(record.color),
                                                                                ).hex,
                                                                            }}
                                                                        ></div>
                                                                    </div>
                                                                ),
                                                            },
                                                        ]}
                                                        dataSource={
                                                            /* @ts-ignore */
                                                            getContourTableParams(contours, recordContour.key)
                                                        }
                                                    />
                                                ),
                                            }}
                                        />
                                    ),
                                }}
                            />
                        ),
                    }}
                />
            </div>
            <div className="flex h-12 min-h-12 shrink-0 items-center justify-between border-y border-gray-500 bg-white px-3 py-2 text-xxs font-semibold uppercase text-black dark:border-fill-primary dark:bg-block-primary dark:text-font-secondary">
                Shape-файл
            </div>
            <div className="shrink-0 grow overflow-y-auto bg-white py-2 dark:bg-block-primary">
                {shapeFiles.map(shape => (
                    <div key={shape.id} className="group flex h-8 items-center justify-between px-4 py-2">
                        <Checkbox
                            checked={shape.visible}
                            onChange={event => applyShapeVisibility(event.target.checked, shape.id)}
                        >
                            {shape.file_name}
                        </Checkbox>
                        <button
                            className="cursor-pointer"
                            onClick={() => deleteShapeFile(Number((shape as any).server_id ?? shape.id))}
                        >
                            <DeleteSVG className="hidden fill-slate-900 group-hover:block" />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};
